import { Box, Grid,Typography,Button,Card, CircularProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useToaster } from 'src/utils/toaster/toasterContext';
import HttpServices from 'src/services/httpService';
import { crmModuleURL, postsModuleURL } from 'src/services/urlService';
import ProfileSelector from './ProfileSelector';
import TriggerConfiguration from './TriggerConfiguration';
import RecentPosts from './RecentPosts';
import { useTranslation } from 'react-i18next';

const AutoDmComponent = ({ handleNavCRMAccounts }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { showToast } = useToaster();
  const { t: translate } = useTranslation();
  const [connectedMedias, setConnectedMedias] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [isLoading, setLoading] = useState(true);
  const [publishedPosts, setPublishedPosts] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);

  useEffect(() => {
    getConnectedNetworks();
  }, []);

  const getConnectedNetworks = () => {
    setLoading(true);
    HttpServices.Get({
      url: crmModuleURL.getCRMAccounts,
      successCallback: (data) => {
        const mappedProfiles = data.map((profile) => ({
          webhook_profile_id: profile.id,
          id: profile.connected_profile_details.connected_profile_id,
          profile_name: profile.connected_profile_details.connected_profile_name,
          profile_image: profile.connected_profile_details.connected_profile_image,
          service_name: profile.connected_profile_details.social_media,
          is_enabled: profile.is_enabled,
        }));
        setConnectedMedias(mappedProfiles);
        const enabledProfile = mappedProfiles.find(
          (profile) => profile.is_enabled &&  profile.profile_type !== "DIRECT PROFILE" && (profile.service_name === 'instagram' || profile.service_name === 'facebook')
        );
        setSelectedProfile(enabledProfile);
        if (enabledProfile) {
          getPublishedPosts(enabledProfile);
        } else {
          setLoading(false);
        }
      },
      failureCallback: (message) => {
        setConnectedMedias([]);
        setSelectedProfile(null);
        setPublishedPosts([]);
        message && showToast(message, 'e');
        setLoading(false);
      },
    });
  };

  const getPublishedPosts = (profile) => {
    setLoading(true);
    let baseUrl;
    switch (profile?.service_name) {
      case 'facebook':
        baseUrl = postsModuleURL.facebookPosts;
        break;
      case 'instagram':
        baseUrl = postsModuleURL.instagramPosts;
        break;
      default:
        setLoading(false);
        showToast('Unsupported social media platform', 'e');
        return;
    }

    HttpServices.Get({
      url: `${baseUrl}${profile.id}&post_limit=20`,
      successCallback: (data) => {
        setPublishedPosts(data.posts || []);
        setLoading(false);
      },
      failureCallback: (message) => {
        setPublishedPosts([]);
        message && showToast(message, 'e');
        setLoading(false);
      },
    });
  };

  const handleProfileChange = (profileId) => {
    const profile = connectedMedias.find((p) => p.id === profileId);
    setSelectedProfile(profile);
    setSelectedPost(null);
    setPublishedPosts([]);
    if (profile) {
      getPublishedPosts(profile);
    }
  };

  return (
    <>
      <Helmet>
        <title>{translate('auto_dm_component.title', 'Auto DM')}</title>
      </Helmet>
      {isLoading && !connectedMedias.length ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 170px)' }}>
          <CircularProgress />
        </Box>
      ) : connectedMedias.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 'calc(100vh - 170px)',
            bgcolor: theme.palette.background.paper,
            borderRadius: 2,
            p: 4,
            textAlign: 'center',
          }}
        >
          <Typography variant="h5" gutterBottom color="text.primary">
            {translate('auto_dm_component.no_accounts_found', 'No Accounts Found')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {translate('auto_dm_component.connect_account_hint', 'Connect a Facebook Page or Instagram account to configure automation triggers.')}
          </Typography>
          <Button variant="contained" color="primary" onClick={handleNavCRMAccounts}>
            {translate('auto_dm_component.connect_account', 'Connect Account')}
          </Button>
        </Box>
      ) : (
        <Card sx={{ p: { xs: 2, md: 3 },height: 'calc(100vh - 140px)' }}>
          <ProfileSelector
            profiles={connectedMedias}
            selectedProfile={selectedProfile}
            onProfileChange={handleProfileChange}
            onNavCRMAccounts={handleNavCRMAccounts}
            isMobile={isMobile}
          />
          {selectedProfile && (
            <Grid container spacing={3} >
              <Grid item xs={12} md={6}>
                <TriggerConfiguration
                  selectedProfile={selectedProfile}
                  selectedPost={selectedPost}
                  posts={publishedPosts}
                  onPostSelect={setSelectedPost}
                  isMobile={isMobile}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <RecentPosts
                  posts={publishedPosts}
                  selectedProfile={selectedProfile}
                  selectedPost={selectedPost}
                  onPostSelect={setSelectedPost}
                  isLoading={isLoading}
                  isMobile={isMobile}
                />
              </Grid>
            </Grid>
          )}
        </Card>
      )}
    </>
  );
};

export default AutoDmComponent;